import {
  type AppModule,
  isAppModule,
  isRole,
  permissionsForRoles,
  type Role,
} from '../access/access.models';
import { normaliseCompanyCode, normaliseCompanyEmail } from '../workspaces/workspaces.mapper';
import type {
  ConfirmEmailResponse,
  CurrentUserResponse,
  RegisterRequest,
  RegisterResponse,
  SessionClaims,
  WorkspaceResponse,
} from './auth.contract';
import type {
  AuthBranch,
  AuthCompany,
  AuthToken,
  AuthUser,
  EmailConfirmation,
  RegistrationDetails,
  RegistrationResult,
} from './auth.models';

export function toAuthUser(
  response: CurrentUserResponse,
  meta?: Readonly<Record<string, unknown>>,
): AuthUser {
  const roles = rolesForSelectedWorkspace(response);

  return {
    id: response.id ?? '',
    email: response.email ?? '',
    displayName: displayNameOf(response),
    firstName: response.first_name ?? '',
    lastName: response.last_name ?? '',
    defaultLanguage: blankTo(response.default_language, 'en'),
    roles,
    permissions: permissionsForRoles(roles),
    featureFlags: [],
    apps: appsOf(meta),
    selectedCompanyId: response.selected_company ?? null,
    selectedBranchId: response.selected_branch ?? null,
    companies: (response.assigned_companies ?? []).map((company) =>
      toCompany(company, rolesByCompany(response)),
    ),
  };
}

/** `meta.apps` when the backend sent a list; unknown names are dropped. */
function appsOf(meta: Readonly<Record<string, unknown>> | undefined): readonly AppModule[] | null {
  const apps = meta?.['apps'];

  return Array.isArray(apps) ? apps.filter(isAppModule) : null;
}

function blankTo(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim() ?? '';

  return trimmed.length > 0 ? trimmed : fallback;
}

function displayNameOf(response: CurrentUserResponse): string {
  const full = response.full_name?.trim();

  if (full) {
    return full;
  }

  const composed = [response.first_name, response.last_name]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0)
    .join(' ');

  return composed.length > 0 ? composed : (response.email ?? '');
}

function rolesForSelectedWorkspace(response: CurrentUserResponse): readonly Role[] {
  const workspaces = response.workspaces ?? [];
  const selected =
    workspaces.find((workspace) => matchesSelection(workspace, response)) ??
    workspaces.find((workspace) => workspace.main === true) ??
    workspaces[0];

  return (selected?.roles ?? [])
    .map((role) => role?.name)
    .filter((name): name is Role => isRole(name));
}

function matchesSelection(workspace: WorkspaceResponse, response: CurrentUserResponse): boolean {
  if (!response.selected_company) {
    return false;
  }

  if (workspace.merchant_id !== response.selected_company) {
    return false;
  }

  return !response.selected_branch || !workspace.branch_id
    ? true
    : workspace.branch_id === response.selected_branch;
}

function toCompany(company: CompanyLike, roles: ReadonlyMap<string, readonly Role[]>): AuthCompany {
  const id = company.id ?? '';

  return {
    id,
    name: company.name ?? '',
    status: company.status ?? '',
    isMain: company.main === true,
    isOwner: company.owner === true,
    isActive: company.is_active !== false,
    branches: (company.branches ?? []).map(toBranch),
    roles: roles.get(id) ?? [],
  };
}

function rolesByCompany(response: CurrentUserResponse): ReadonlyMap<string, readonly Role[]> {
  const index = new Map<string, Role[]>();

  for (const workspace of response.workspaces ?? []) {
    const companyId = workspace.merchant_id ?? '';

    if (companyId.length === 0) {
      continue;
    }

    const bucket = index.get(companyId) ?? [];

    for (const role of workspace.roles ?? []) {
      const name = role?.name;

      if (isRole(name) && !bucket.includes(name)) {
        bucket.push(name);
      }
    }

    index.set(companyId, bucket);
  }

  return index;
}

function toBranch(branch: BranchLike): AuthBranch {
  return {
    id: branch.id ?? '',
    name: branch.name ?? '',
    isMain: branch.main === true,
  };
}

type CompanyLike = NonNullable<CurrentUserResponse['assigned_companies']>[number];
type BranchLike = NonNullable<CompanyLike['branches']>[number];

export function toRegisterPayload(details: RegistrationDetails): RegisterRequest {
  const isEmployer = details.roleName === 'employer';

  return {
    user: {
      first_name: details.firstName.trim(),
      last_name: details.lastName.trim(),
      mid_name: details.midName.trim(),
      email: details.email.trim(),
      password: details.password,
      confirm_password: details.confirmPassword,
      status: 'active',
      role_name: details.roleName,
    },
    company: isEmployer
      ? {
          name: (details.companyName ?? '').trim(),
          email: normaliseCompanyEmail(details.companyEmail ?? ''),
          code: normaliseCompanyCode(details.companyCode ?? ''),
        }
      : null,
    company_token: isEmployer ? '' : (details.companyToken ?? '').trim(),
  };
}

/**
 * A token in the answer means an older backend that still signs the new
 * account straight in; anything else means a confirmation link is on its way.
 * `sentTo` is the address the form submitted, used when the answer omits it.
 */
export function toRegistrationResult(
  response: RegisterResponse | null | undefined,
  sentTo: string,
): RegistrationResult {
  const token = response?.token?.trim() ?? '';

  if (token.length > 0) {
    return { confirmationRequired: false, token: toAuthToken(token) };
  }

  return { confirmationRequired: true, email: blankTo(response?.email, sentTo.trim()) };
}

export function toEmailConfirmation(
  response: ConfirmEmailResponse | null | undefined,
): EmailConfirmation {
  return { alreadyConfirmed: response?.already_confirmed === true };
}

export function toAuthToken(accessToken: string): AuthToken {
  const claims = decodeClaims(accessToken);
  const exp = claims?.exp;

  return {
    accessToken,
    expiresAt: typeof exp === 'number' ? exp * 1000 : null,
  };
}

export function decodeClaims(token: string): SessionClaims | null {
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(Math.ceil(normalised.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes)) as SessionClaims;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: AuthToken | null, skewMs = 30_000): boolean {
  if (token === null) {
    return true;
  }

  return token.expiresAt === null ? false : Date.now() + skewMs >= token.expiresAt;
}
